import { showChangeModal } from "../components/ChangeModal";

import { ToolType } from "./types";

import { MAX_PAGE_OUTCOMES } from "components/constants";
import { RubricList } from "components/outcomes/RubricList";
import { t } from "i18n/i18n";
import { docCache } from "sharedb/ShareDBDoc";
import { store, storeApi } from "store/index";
import {
  selectCurrentOrg,
  selectOutcomes,
  selectPageOutcomes,
  selectPages,
  selectSectionOutcomes,
} from "store/selectors";
import { BROADCAST_TOPICS, sendBroadcast } from "utils/broadcast";

/**
 * Takes outcome titles and rubric descriptions and ensures they are set on the current Page,
 * removing any that are not given.
 */
export const modifyPageRubric = async (params: {
  outcome_rubrics: { outcome: string; rubric: string[] }[];
}) => {
  const state = store.getState();
  const doc = docCache.current;
  if (!doc) {
    throw new Error("Worksheet for current Page not found");
  }
  const pageId = selectPages(state).find((p) => p.worksheet_id === doc?.id)?.id;
  if (!pageId) {
    throw new Error("Current Page not found");
  }

  // Limiting outcomes, if more than max were attempted to tag, we'll just return the max
  const outcomeTitles = params.outcome_rubrics.map((o) => o.outcome).slice(0, MAX_PAGE_OUTCOMES);

  if (outcomeTitles.length === 0) {
    throw new Error("Failed to set outcome rubrics.");
  }

  const orgRubric = selectCurrentOrg(state).rubric;
  const outcomes = selectOutcomes(state);
  const sectionOutcomes = selectSectionOutcomes(state);
  const pageOutcomes = selectPageOutcomes(state);
  const existingPageOutcomes = pageOutcomes.filter((po) => po.page_id === pageId);
  const existingOutcomeRubrics = existingPageOutcomes.map((po) => ({
    pageOutcomeId: po.id,
    rubric: po.rubric_descriptions,
  }));

  const outcomeRubrics = params.outcome_rubrics
    .map((outcomeRubric) => {
      const outcome = outcomes.find((o) => o.title === outcomeRubric.outcome.replace(/^#/, ""));
      const sectionOutcome = sectionOutcomes.find((co) => co.outcome_id === outcome?.id);
      const pageOutcome = existingPageOutcomes.find(
        (po) => po.section_outcome_id === sectionOutcome?.id,
      );
      return {
        pageOutcomeId: pageOutcome?.id || "",
        rubric: outcomeRubric.rubric,
      };
    })
    .filter((o) => o.pageOutcomeId);

  if (outcomeRubrics.length === 0) {
    throw new Error("Failed to set outcome rubrics.");
  }

  const outcomeRubricToRubricItem = (outcomeRubric: {
    pageOutcomeId: string;
    rubric: string[];
  }) => {
    const pageOutcome = pageOutcomes.find((o) => o.id === outcomeRubric.pageOutcomeId);
    const sectionOutcome = sectionOutcomes.find((o) => o.id === pageOutcome?.section_outcome_id);
    const outcome = outcomes.find((o) => o.id === sectionOutcome?.outcome_id);
    const outcomeTitle = outcome?.title;
    return (
      <div key={outcomeRubric.pageOutcomeId}>
        <h3>{outcomeTitle}</h3>
        <RubricList
          outcomeItem={outcome}
          rubric={outcomeRubric.rubric.map((r, i) => ({
            name: orgRubric[i].name,
            description: r,
            score: i,
            conversion_percent: 100,
          }))}
          showHeader={false}
          readOnly
        />
      </div>
    );
  };

  const accepted = await showChangeModal(
    <div>{existingOutcomeRubrics.map(outcomeRubricToRubricItem)}</div>,
    <div>{outcomeRubrics.map(outcomeRubricToRubricItem)}</div>,
  );

  if (!accepted) {
    return t("ai.modification_rejected");
  }

  await Promise.all(
    outcomeRubrics.map((outcome) =>
      storeApi.page_outcomes.partial_update({
        id: outcome.pageOutcomeId,
        rubric_descriptions: outcome.rubric,
      }),
    ),
  );

  // This allows for animation on the Page to show that outcomes have been tagged
  sendBroadcast(BROADCAST_TOPICS.AI_RUBRIC_GENERATED, { pageId });

  return t("ai.modification_accepted");
};

export const modifyPageRubricTool: ToolType<{
  outcome_rubrics: { outcome: string; rubric: string[] }[];
}> = {
  name: "modify_page_rubric",
  description: "Set the rubric for specific Outcomes on the current Page.",
  progressMessage: "Adjusting outcome rubrics for the current Page",
  parameters: {
    type: "object",
    properties: {
      outcome_rubrics: {
        type: "array",
        minItems: 1,
        maxItems: MAX_PAGE_OUTCOMES,
        items: {
          type: "object",
          properties: {
            outcome: {
              type: "string",
              description: "The title of the outcome the rubric is for.",
            },
            rubric: {
              type: "array",
              minItems: 4,
              maxItems: 6,
              description:
                "The rubric entries for the outcome, in order of lowest score to highest score.",
              items: {
                type: "string",
                description:
                  "One rubric entry. Should NOT be prefixed with the index or any numbering.",
              },
            },
          },
          required: ["outcome", "rubric"],
          additionalProperties: false,
        },
        description: "A list of outcome and rubric pairs for each outcome on the Page.",
      },
    },
    required: ["outcome_rubrics"],
    additionalProperties: false,
  },
  callback: modifyPageRubric,
};
